import { DAY_MS, toMillis } from './dates';
import { round2 } from './money';
import { loadDate } from '../services/archiveExport';

/** Statuses in the order an invoice moves through them. */
export const REPORT_STATUSES = ['pending', 'pay_to_driver', 'PAID'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export interface StatusSlice {
  status: ReportStatus;
  count: number;
  amount: number;
}

export interface AgingBucket {
  label: string;
  count: number;
  amount: number;
}

export interface MonthTotal {
  /** e.g. "2026-03", for sorting */
  key: string;
  label: string;
  count: number;
  amount: number;
}

export interface BrokerRow {
  broker: string;
  loads: number;
  billed: number;
  pending: number;
  payToDriver: number;
  paid: number;
  /** Mean age in days of this broker's pending invoices; null if none */
  averagePendingDays: number | null;
}

export interface DriverRow {
  dogtag: string;
  loads: number;
  billed: number;
  pending: number;
  payToDriver: number;
  paid: number;
}

export interface ReportSummary {
  loads: number;
  billed: number;
  gallons: number;
  demurrage: number;
  averageLoad: number;
  statuses: StatusSlice[];
  aging: AgingBucket[];
  averagePendingDays: number;
  months: MonthTotal[];
  brokers: BrokerRow[];
  drivers: DriverRow[];
}

const amountOf = (invoice: any): number => {
  const value = Number(invoice.totalCharges);
  return isNaN(value) ? 0 : value;
};

const numberOf = (value: any): number => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const statusOf = (invoice: any): ReportStatus =>
  (REPORT_STATUSES as readonly string[]).includes(invoice.status)
    ? (invoice.status as ReportStatus)
    : 'pending';

const AGING_BUCKETS: Array<{ label: string; max: number }> = [
  { label: '0–30 days', max: 30 },
  { label: '31–60 days', max: 60 },
  { label: '61–90 days', max: 90 },
  { label: '90+ days', max: Infinity }
];

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Everything the Reports page shows, derived from the invoices in hand. */
export const computeReport = (invoices: any[], now: number = Date.now()): ReportSummary => {
  const statuses: Record<ReportStatus, StatusSlice> = {
    pending: { status: 'pending', count: 0, amount: 0 },
    pay_to_driver: { status: 'pay_to_driver', count: 0, amount: 0 },
    PAID: { status: 'PAID', count: 0, amount: 0 }
  };
  const aging = AGING_BUCKETS.map(bucket => ({ label: bucket.label, count: 0, amount: 0 }));
  const months: Record<string, MonthTotal> = {};
  const brokers: Record<string, BrokerRow & { pendingAgeTotal: number; pendingAgeCount: number }> = {};
  const drivers: Record<string, DriverRow> = {};

  let billed = 0;
  let gallons = 0;
  let demurrage = 0;
  let pendingAgeTotal = 0;
  let pendingAgeCount = 0;

  invoices.forEach(invoice => {
    const amount = amountOf(invoice);
    const status = statusOf(invoice);

    billed += amount;
    gallons += numberOf(invoice.gross);
    demurrage += numberOf(invoice.demurrageCharge);

    statuses[status].count += 1;
    statuses[status].amount += amount;

    // Aging counts how long pending invoices have been on the books.
    let ageDays: number | null = null;
    if (status === 'pending') {
      const created = toMillis(invoice.createdAt);
      if (created) {
        ageDays = Math.max(0, (now - created) / DAY_MS);
        pendingAgeTotal += ageDays;
        pendingAgeCount += 1;
        const index = AGING_BUCKETS.findIndex(bucket => (ageDays as number) <= bucket.max);
        aging[index].count += 1;
        aging[index].amount += amount;
      }
    }

    const date = loadDate(invoice);
    if (date) {
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const month = months[key] || {
        key,
        label: `${SHORT_MONTHS[date.getMonth()]} ${String(date.getFullYear()).slice(2)}`,
        count: 0,
        amount: 0
      };
      month.count += 1;
      month.amount += amount;
      months[key] = month;
    }

    const brokerName = (invoice.broker || '').trim() || 'No broker';
    const broker = brokers[brokerName] || {
      broker: brokerName,
      loads: 0,
      billed: 0,
      pending: 0,
      payToDriver: 0,
      paid: 0,
      averagePendingDays: null,
      pendingAgeTotal: 0,
      pendingAgeCount: 0
    };
    broker.loads += 1;
    broker.billed += amount;
    if (status === 'pending') broker.pending += amount;
    if (status === 'pay_to_driver') broker.payToDriver += amount;
    if (status === 'PAID') broker.paid += amount;
    if (ageDays !== null) {
      broker.pendingAgeTotal += ageDays;
      broker.pendingAgeCount += 1;
    }
    brokers[brokerName] = broker;

    const dogtag = invoice.driver || '';
    const driver = drivers[dogtag] || {
      dogtag,
      loads: 0,
      billed: 0,
      pending: 0,
      payToDriver: 0,
      paid: 0
    };
    driver.loads += 1;
    driver.billed += amount;
    if (status === 'pending') driver.pending += amount;
    if (status === 'pay_to_driver') driver.payToDriver += amount;
    if (status === 'PAID') driver.paid += amount;
    drivers[dogtag] = driver;
  });

  return {
    loads: invoices.length,
    billed: round2(billed),
    gallons: Math.round(gallons),
    demurrage: round2(demurrage),
    averageLoad: invoices.length ? round2(billed / invoices.length) : 0,
    statuses: REPORT_STATUSES.map(status => ({
      ...statuses[status],
      amount: round2(statuses[status].amount)
    })),
    aging: aging.map(bucket => ({ ...bucket, amount: round2(bucket.amount) })),
    averagePendingDays: pendingAgeCount ? Math.round(pendingAgeTotal / pendingAgeCount) : 0,
    months: Object.values(months)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(month => ({ ...month, amount: round2(month.amount) })),
    brokers: Object.values(brokers)
      .map(({ pendingAgeTotal: total, pendingAgeCount: count, ...row }) => ({
        ...row,
        billed: round2(row.billed),
        pending: round2(row.pending),
        payToDriver: round2(row.payToDriver),
        paid: round2(row.paid),
        averagePendingDays: count ? Math.round(total / count) : null
      }))
      .sort((a, b) => b.billed - a.billed),
    drivers: Object.values(drivers)
      .map(row => ({
        ...row,
        billed: round2(row.billed),
        pending: round2(row.pending),
        payToDriver: round2(row.payToDriver),
        paid: round2(row.paid)
      }))
      .sort((a, b) => b.billed - a.billed)
  };
};
